import {
  Layers,
  MessageSquareWarning,
  Plus,
  Send,
  Target,
  Trash2,
} from 'lucide-react';
import * as React from 'react';
import { canAddIndividualGoal, MAX_INDIVIDUAL_GOALS } from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  Skeleton,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import { WeightageMeter } from '@/components/goals/weightage-meter';
import { goalAssignmentVariant, PILL_CLASS } from '@/lib/badges';
import { goalLibrary, useAssignKraGroup, useKraGroups } from '@/lib/kra-groups-api';
import {
  useAddGoal,
  useAddKra,
  useDeleteGoal,
  useDeleteKra,
  useGoalSet,
  usePublishGoals,
  useRequestGoalChanges,
  useGoalHeading,
  useUpdateGoal,
  type Goal,
  type GoalSet,
} from '@/lib/goals-api';

const STATUS_LABEL = {
  DRAFT: 'Draft',
  // Legacy: sets that were waiting on an employee's click when acceptance was removed.
  // Live, like ACCEPTED — labelled the same so nobody goes looking for a button to press.
  PENDING_ACCEPTANCE: 'Live',
  ACCEPTED: 'Live',
} as const;

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

/**
 * A KRA description, rendered as bullets.
 *
 * The spec asks for a bullet list, and a line break is the only markup anyone will
 * reliably type. Splitting on newlines keeps the stored value plain text - no
 * markdown parser, and nothing to sanitise, because nothing is interpreted.
 */
function KraDescription({ text }: { text: string | null }) {
  const points = (text ?? '')
    .split('\n')
    // Tolerate a pasted list that already carries its own bullet characters.
    .map((line) => line.replace(/^\s*[-*•]\s*/, '').trim())
    .filter((line) => line.length > 0);

  if (points.length === 0) return null;

  return (
    <ul className="mt-0.5 space-y-0.5">
      {points.map((point, i) => (
        <li key={i} className="flex gap-1.5 text-xs text-muted-foreground">
          <span className="mt-1.5 size-1 shrink-0 rounded-full bg-muted-foreground" />
          <span>{point}</span>
        </li>
      ))}
    </ul>
  );
}

/** Marks a pick as one goal out of the library rather than a whole KRA. */
const SINGLE = 'single:';

/**
 * Take something from the KRA library — a whole KRA, or one goal out of one.
 *
 * One dropdown for both, because they are the same intent at two sizes and splitting
 * them into two controls made "reuse what we already wrote" a thing you had to know
 * where to look for. The headings say which is which, and the hint says what a pick
 * will actually do before it does it.
 *
 * Assigning is idempotent and reports what happened rather than a bare "done":
 * "3 added, 2 already there" is the difference between a useful second click and one
 * that looks broken.
 */
function AssignFromLibrary({
  goalId,
  employeeId,
}: {
  goalId: string;
  employeeId: string | 'me';
}) {
  const { data: groups } = useKraGroups();
  const assign = useAssignKraGroup(employeeId);
  const addKra = useAddKra(employeeId);

  if (!groups || groups.length === 0) return null;
  const goals = goalLibrary(groups);

  return (
    <Combobox
      // Select-only: the library is authored on Manage KRA, where a KRA gets the goals
      // that make it worth assigning. The box above is where a new one gets typed.
      value={null}
      contentClassName="w-80"
      searchPlaceholder="Search the KRA library"
      emptyText="Nothing in the library matches"
      options={[
        ...groups.map((group) => ({
          value: group.id,
          label: group.name,
          hint: `${group.items.length} goals`,
          group: 'Whole KRAs',
        })),
        ...goals.map((goal) => ({
          value: `${SINGLE}${goal.id}`,
          label: goal.name,
          group: 'Single goals',
        })),
      ]}
      onChange={async (value) => {
        if (value.startsWith(SINGLE)) {
          const goal = goals.find((g) => `${SINGLE}${g.id}` === value);
          if (!goal) return;
          await addKra.mutateAsync({ goalId, name: goal.name, description: goal.description });
          toast.success(`"${goal.name}" added`);
          return;
        }
        const { added, skipped } = await assign.mutateAsync({ groupId: value, goalId });
        toast.success(
          added === 0
            ? 'Every KRA in that one is already on this goal'
            : `${added} KRA(s) added${skipped > 0 ? `, ${skipped} already there` : ''}`,
        );
      }}
      trigger={
        <Button hint={"Pick goals from the shared KRA library"} type="button" variant="ghost" size="sm" className="gap-2 text-xs">
          <Layers className="size-3.5" />
          Add from the KRA library
        </Button>
      }
    />
  );
}

/** KRA list plus an inline add box. */
function KraList({
  goal,
  employeeId,
  editable,
}: {
  goal: Goal;
  employeeId: string | 'me';
  editable: boolean;
}) {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const addKra = useAddKra(employeeId);
  const deleteKra = useDeleteKra(employeeId);

  return (
    <div className="space-y-1.5 pl-4">
      {goal.kras.map((kra) => (
        <div key={kra.id} className="flex items-start gap-2 text-sm">
          <span className="text-muted-foreground">↳</span>
          <div className="flex-1">
            <span>{kra.name}</span>
            <KraDescription text={kra.description} />
          </div>
          {/* This KRA's share of the goal, shown only once some KRA under it has one -
              all-zero means they count equally and a row of "0%" would read as the
              opposite.

              ponytail: read-only. A KRA added by hand below lands at 0, so inside an
              already-split goal it carries no weight and says so. Re-split it in the KRA
              library; wire the number up here if that turns out to be the common case
              (the API already takes `weightage` on both KRA routes). */}
          {goal.kras.some((k) => k.weightage > 0) && (
            <span className="pms-num shrink-0 pt-px text-xs text-muted-foreground">
              {kra.weightage}%
            </span>
          )}
          {editable && (
            <ConfirmButton
              variant="ghost"
              size="icon"
              aria-label={`Remove ${kra.name}`}
              title={`Remove "${kra.name}"?`}
              // Deleting a KRA cascades its ratings away, which is the part nobody
              // expects from a bin icon on a row.
              description="Any rating already given against this KRA goes with it, and the goal's rating is then computed from what is left. To retire one that has been rated, archive it instead."
              confirmLabel="Remove"
              destructive
              onConfirm={() => {
                void deleteKra
                  .mutateAsync(kra.id)
                  .catch((e: unknown) => toast.error('Could not remove KRA', { description: err(e) }));
              }}
            >
              <Trash2 className="size-3.5" />
            </ConfirmButton>
          )}
        </div>
      ))}

      {editable && (
        <form
          className="space-y-1.5 pt-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            void addKra
              .mutateAsync({
                goalId: goal.id,
                name: name.trim(),
                description: description.trim() || null,
              })
              .then(() => {
                setName('');
                setDescription('');
              })
              .catch((x: unknown) => toast.error('Could not add KRA', { description: err(x) }));
          }}
        >
          <div className="flex gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Add a key result area"
              className="h-8 text-sm"
            />
            <Button type="submit" size="sm" variant="outline" disabled={addKra.isPending}>
              Add
            </Button>
          </div>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Description — one point per line, shown as bullets"
            className="text-sm"
          />
          <AssignFromLibrary goalId={goal.id} employeeId={employeeId} />
        </form>
      )}

      {!editable && goal.kras.length === 0 && (
        <p className="text-xs text-muted-foreground">No KRAs yet.</p>
      )}
    </div>
  );
}

function GoalRow({
  goal,
  employeeId,
  editable,
  showWeightage,
}: {
  goal: Goal;
  employeeId: string | 'me';
  editable: boolean;
  showWeightage: boolean;
}) {
  const updateGoal = useUpdateGoal(employeeId);
  const deleteGoal = useDeleteGoal(employeeId);
  const { heading, sub } = useGoalHeading()(goal);
  const [weightage, setWeightage] = React.useState(String(goal.weightage));

  // The server is the source of truth; if it rejects an edit the input must snap
  // back rather than keep showing a value that was never saved.
  React.useEffect(() => setWeightage(String(goal.weightage)), [goal.weightage]);

  function commitWeightage() {
    const next = Number(weightage);
    if (!Number.isInteger(next) || next === goal.weightage) {
      setWeightage(String(goal.weightage));
      return;
    }
    void updateGoal
      .mutateAsync({ goalId: goal.id, weightage: next })
      .catch((e: unknown) => {
        setWeightage(String(goal.weightage));
        toast.error('Could not update weightage', { description: err(e) });
      });
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-start gap-3">
        <Target className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{heading}</p>
          {sub && <p className="mt-0.5 text-sm text-muted-foreground">{sub}</p>}
        </div>

        {showWeightage &&
          (editable ? (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={1}
                max={100}
                step={1}
                value={weightage}
                onChange={(e) => setWeightage(e.target.value)}
                onBlur={commitWeightage}
                className="h-8 w-16 text-right"
                aria-label={`Weightage for ${heading}`}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          ) : (
            <Badge variant="soft-muted" className={PILL_CLASS}>
              {goal.weightage}%
            </Badge>
          ))}

        {editable && (
          <ConfirmButton
            variant="ghost"
            size="icon"
            aria-label={`Delete ${heading}`}
            title={`Delete "${heading}"?`}
            description={
              goal.weightage > 0
                ? `Its ${goal.weightage}% comes off the set, so the remaining goals no longer total 100 and the set cannot be published until they do. Every KRA under it goes too, along with any rating already given against them.`
                : 'Every KRA under it goes too, along with any rating already given against them.'
            }
            confirmLabel="Delete goal"
            destructive
            onConfirm={() => {
              void deleteGoal
                .mutateAsync(goal.id)
                .catch((e: unknown) => toast.error('Could not delete goal', { description: err(e) }));
            }}
          >
            <Trash2 className="size-4" />
          </ConfirmButton>
        )}
      </div>

      {/* KRAs hang off organization goals only - the server rejects them anywhere
          else, and individual goals carry 0 weightage so nothing would rate them. */}
      <KraList
        goal={goal}
        employeeId={employeeId}
        editable={editable && goal.type === 'ORGANIZATIONAL'}
      />
    </div>
  );
}

function AddGoalForm({
  employeeId,
  assignmentId,
  type,
}: {
  employeeId: string | 'me';
  assignmentId: string;
  type: 'ORGANIZATIONAL' | 'INDIVIDUAL';
}) {
  const [title, setTitle] = React.useState('');
  const [weightage, setWeightage] = React.useState('');
  const addGoal = useAddGoal(employeeId, assignmentId);

  return (
    <form
      className="flex flex-wrap gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        void addGoal
          .mutateAsync({
            type,
            title: title.trim(),
            weightage: type === 'INDIVIDUAL' ? 0 : Number(weightage || 0),
          })
          .then(() => {
            setTitle('');
            setWeightage('');
          })
          .catch((x: unknown) => toast.error('Could not add goal', { description: err(x) }));
      }}
    >
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        data-guide={type === 'INDIVIDUAL' ? 'dev-goal-title' : undefined}
        placeholder={
          type === 'ORGANIZATIONAL'
            ? 'e.g. Improve shipment accuracy'
            : 'e.g. AWS certification'
        }
        className="min-w-[200px] flex-1"
      />
      {type === 'ORGANIZATIONAL' && (
        <Input
          type="number"
          min={1}
          max={100}
          step={1}
          value={weightage}
          onChange={(e) => setWeightage(e.target.value)}
          placeholder="%"
          className="w-20"
          aria-label="Weightage"
        />
      )}
      <Button type="submit" data-guide={type === 'INDIVIDUAL' ? 'dev-goal-add' : undefined} disabled={addGoal.isPending} className="gap-1.5">
        <Plus className="size-4" />
        Add
      </Button>
    </form>
  );
}

/**
 * The goal set, for your own goals and for a report's.
 *
 * Every permission comes from the server (canEditOrganizational, canSend, ...)
 * rather than being re-derived from the viewer's role here - the API decides, and
 * a second copy of that logic in the client is how the two drift apart.
 */
/**
 * "Ask for changes" - the other answer to a goal set.
 *
 * Deliberately not a plain button: the note is the whole point. A request that only
 * says "no" leaves the manager guessing, and they are the one who has to act on it.
 */
function RequestChanges({ set, employeeId }: { set: GoalSet; employeeId: string | 'me' }) {
  const request = useRequestGoalChanges(employeeId, set.assignmentId);
  const [open, setOpen] = React.useState(false);
  const [note, setNote] = React.useState('');

  if (!open) {
    return (
      <Button hint={"Ask your manager to change these goals"} variant="outline" className="gap-2" onClick={() => setOpen(true)}>
        <MessageSquareWarning className="size-4" />
        Ask for changes
      </Button>
    );
  }

  return (
    <form
      className="w-full max-w-md space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!note.trim()) return;
        void request
          .mutateAsync({ note: note.trim() })
          .then(() => {
            setNote('');
            setOpen(false);
            toast.success('Change request sent', {
              description: set.locked
                ? 'HR has been told — these goals have been rated, so only they can change them.'
                : 'Your manager can now edit these goals. They stay live meanwhile.',
            });
          })
          .catch((x: unknown) => toast.error('Could not send the request', { description: err(x) }));
      }}
    >
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        autoFocus
        placeholder="What needs to change, and why?"
        className="text-sm"
      />
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button hint={"Send the change request to your manager"} type="submit" size="sm" disabled={!note.trim() || request.isPending}>
          Send request
        </Button>
        {!note.trim() && <span className="self-center text-xs text-muted-foreground">Say what should change</span>}
      </div>
    </form>
  );
}

export function GoalSetView({ employeeId }: { employeeId: string | 'me' }) {
  const { data: set, isPending, error } = useGoalSet(employeeId);

  const publish = usePublishGoals(employeeId, set?.assignmentId ?? '');

  if (isPending) return <Skeleton className="h-64 w-full" />;
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  const orgGoals = set.goals.filter((g) => g.type === 'ORGANIZATIONAL');
  const individualGoals = set.goals.filter((g) => g.type === 'INDIVIDUAL');

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              Organization goals
              <Badge variant={goalAssignmentVariant(set.status)} className={PILL_CLASS}>
                {STATUS_LABEL[set.status]}
              </Badge>
            </CardTitle>
            <CardDescription>
              {set.cycleLabel} · set by the reporting manager · drives the official rating
              {set.assignedByName ? ` · assigned by ${set.assignedByName}` : ''}
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-start justify-end gap-2">
            {set.canRequestChanges && <RequestChanges set={set} employeeId={employeeId} />}
          </div>

          {/* No accept button, by design: the organization sets these goals, so the
              employee is told rather than asked. Re-publishing is how a correction
              reaches a live set, so this stays available until the first rating. */}
          {set.canEditOrganizational && (
            <ConfirmButton
              className="gap-2"
              disabled={!set.canSend || publish.isPending}
              title={
                set.status === 'DRAFT'
                  ? `Publish ${set.employeeName}'s goals?`
                  : `Re-publish ${set.employeeName}'s goals?`
              }
              description={
                set.status === 'DRAFT'
                  ? `${set.employeeName} is emailed that their goals are live, and the ${set.validation.orgTotalWeightage}% split across ${set.validation.orgCount} goals becomes what their rating is computed from.`
                  : `${set.employeeName} is emailed again, and any outstanding change request they raised is cleared. The set they are measured on becomes what is on this screen now.`
              }
              confirmLabel={set.status === 'DRAFT' ? 'Publish goals' : 'Re-publish'}
              pending={publish.isPending}
              onConfirm={() => {
                void publish
                  .mutateAsync()
                  .then(() =>
                    toast.success(
                      set.status === 'DRAFT' ? 'Goals published' : 'Published changes',
                      { description: 'The employee has been notified.' },
                    ),
                  )
                  .catch((e: unknown) => toast.error('Could not publish', { description: err(e) }));
              }}
            >
              <Send className="size-4" />
              {set.status === 'DRAFT' ? 'Publish goals' : 'Re-publish'}
            </ConfirmButton>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {set.canEditOrganizational && (
            <WeightageMeter total={set.validation.orgTotalWeightage} goalCount={orgGoals.length} />
          )}

          {set.changeRequestNote && (
            // Amber, like an HR override: someone pushed back on this set, and the
            // person who has to act on it should not have to open a notification to
            // find out what they asked for.
            <Alert>
              <MessageSquareWarning className="size-4" />
              <AlertDescription className="text-sm">
                <span className="font-medium">Changes requested</span>
                {set.changeRequestedAt
                  ? ` on ${new Date(set.changeRequestedAt).toLocaleDateString()}`
                  : ''}
                : {set.changeRequestNote}
              </AlertDescription>
            </Alert>
          )}

          {set.status !== 'DRAFT' && !set.canEditOrganizational && (
            <Alert>
              <AlertDescription className="text-sm">
                {set.locked
                  ? 'These goals have been rated, so the weightage split your rating is computed from is settled. Ask HR if something needs to change.'
                  : 'This is what you are measured on this year. Your manager can still correct it — ask for a change if something looks wrong.'}
              </AlertDescription>
            </Alert>
          )}

          {orgGoals.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No organization goals yet.
            </p>
          )}

          {orgGoals.map((goal) => (
            <GoalRow
              key={goal.id}
              goal={goal}
              employeeId={employeeId}
              editable={set.canEditOrganizational}
              showWeightage
            />
          ))}

          {set.canEditOrganizational && (
            <AddGoalForm
              employeeId={employeeId}
              assignmentId={set.assignmentId}
              type="ORGANIZATIONAL"
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Development goals</CardTitle>
          <CardDescription>
            Your own goals — certifications, skills, courses. 0% weightage: they never affect the
            rating, and appear at appraisal as supporting evidence.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {individualGoals.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No development goals yet.
            </p>
          )}

          {individualGoals.map((goal) => (
            <GoalRow
              key={goal.id}
              goal={goal}
              employeeId={employeeId}
              editable={set.canEditIndividual}
              showWeightage={false}
            />
          ))}

          {set.canEditIndividual &&
            (canAddIndividualGoal(individualGoals.length) ? (
              <AddGoalForm
                employeeId={employeeId}
                assignmentId={set.assignmentId}
                type="INDIVIDUAL"
              />
            ) : (
              // The API rejects a third too. This only avoids offering a button
              // whose sole outcome is an error toast.
              <p className="text-xs text-muted-foreground">
                You have the maximum of {MAX_INDIVIDUAL_GOALS} development goals. Remove one to add
                another.
              </p>
            ))}
        </CardContent>
      </Card>
    </div>
  );
}
